import React, { useEffect, useMemo, useRef, useState } from "react";
import useShellScrollLock from "../../hooks/useShellScrollLock";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import routes from "../../routes";
import NotFound from "../../Views/NotFound/NotFound";
import { AdminPageSkeleton } from "../Loading/AdminDataSkeleton";
import Sidebar from "../Sidebar";
import { MainNav } from "../MuiTable";
import { useMediaQuery } from "@mui/material";
import { Grid } from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
// JOB-7066: merchant branding (palette for the viewer's appearance, fonts).
import { BrandContext, useCompanyBrand } from "../../contexts/BrandContext";
import { withBrandTheme } from "../../utils/paintBrand";
import { updateCompanyNameFromToken, setPageTitle, getPageTitleFromRoute } from "../../utils/pageTitle";
import {
  CJ_PORTAL_SIDEBAR_COLLAPSED_PX,
  CJ_PORTAL_SIDEBAR_EXPANDED_PX,
} from "../../utils/cjPortalSidebarMetrics";
import {
  CustomerDarkModeProvider,
  useCustomerDarkMode,
} from "../../contexts/CustomerDarkModeContext";
import "./admin-dark-overrides.css";
// JOB-5706: the customer portal loads admin-dark-overrides.css and sets
// body.admin-dark-active, so the contractor-amber profile-tab rule reaches it.
// It needs the same donation-mode class the admin shell now carries, or the CDM
// customer profile keeps an amber tab despite branching correctly in its sx.
import { useUserType } from "../../contexts/UserTypeContext";
// LIMO-203: useUserType cannot say "limo" by design — its legacy shape has
// only isDonor, and a limo tenant reads as the contractor side of it. The
// product resolver is the only thing that knows.
import { useProduct } from "../../contexts/ProductContext";
import useLimoTheme from "../../hooks/useLimoTheme";
import { applyColorScheme } from "../../utils/colorScheme";

function ClientInner() {
  const { darkMode } = useCustomerDarkMode();
  const brand = useCompanyBrand(darkMode);
  // JOB-5706: host-derived donation mode, same source the shell brands from.
  const { isDonor: isDonationMode } = useUserType();
  const { isLimo } = useProduct();
  const mainContent = useRef(null);
  const scrollShell = useRef(null);
  // JOB-7517: the shell is the only scroller; the document must not scroll (see useShellScrollLock).
  useShellScrollLock();
  const location = useLocation();
  const { CompanyUrl } = useParams();
  const [isSidebarClosed, setIsSidebarClosed] = useState(false);
  const [isSidebarDisplay, setIsSidebarDisplay] = useState(true);
  // `noSsr` so the first render reads the real viewport width instead of the
  // SSR default (false) — without this, mobile pages briefly mount with the
  // desktop sidebar marginLeft applied, squeezing content into a ~135px column.
  const isMediumScreen = useMediaQuery("(max-width:767px)", { noSsr: true });

  // Reset sidebar state when screen size changes
  useEffect(() => {
    if (!isMediumScreen) {
      setIsSidebarDisplay(true);
      setIsSidebarClosed(false);
    } else {
      setIsSidebarDisplay(false);
    }
  }, [isMediumScreen]);

  useEffect(() => {
    // JOB-5088: the real scroller is the outer 100vh/overflow:auto
    // `.admin-shell` Grid, not `mainContent` (no scroll context).
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    if (mainContent.current) {
      mainContent.current.scrollTop = 0;
    }
    if (scrollShell.current) {
      scrollShell.current.scrollTop = 0;
    }

    const title = getPageTitleFromRoute(location.pathname);
    setPageTitle(title);
  }, [location]);

  useEffect(() => {
    if (darkMode) {
      document.body.classList.add("admin-dark-active");
    } else {
      document.body.classList.remove("admin-dark-active");
    }
    // LIMO-205: <html> has to say the same thing the page looks like. The
    // browser reads color-scheme for native controls and scrollbars, and CSS
    // reads the attribute; index.html sets both to light before React boots
    // and nothing used to update them afterwards.
    applyColorScheme(darkMode);
    return () => {
      document.body.classList.remove("admin-dark-active");
      applyColorScheme(false);
    };
  }, [darkMode]);

  // JOB-5706 — see the import note above. Mirrors the effect in Admin.jsx.
  useEffect(() => {
    if (isDonationMode) {
      document.body.classList.add("admin-donation-mode");
    } else {
      document.body.classList.remove("admin-donation-mode");
    }
    return () => document.body.classList.remove("admin-donation-mode");
  }, [isDonationMode]);

  /* LIMO-203 — the same effect Admin.jsx has, which the portal never got.
     Every limo palette rule is scoped to `body.admin-limo-mode` or
     `.admin-shell--limo`, and both hooks were attached only by Admin.jsx and
     Auth.jsx. So 43 CSS rules were inert in the customer portal and it wore
     contractor colours: the account menu came out the contractor amber
     #D4943A, with its text at 2.46:1 and its avatar initials at 1:1, while a
     correct limo override for that exact menu already sat unused in
     MuiTable/style.css. The class on the body matters as much as the one on
     the shell below, because MUI portals half the chrome straight to <body>. */
  const limoShell = useLimoTheme(isLimo);

  const portalTheme = useMemo(
    () =>
      createTheme(
        withBrandTheme({
          palette: {
            mode: darkMode ? "dark" : "light",
            primary: {
              main: darkMode ? "#90caf9" : "#1976d2", // brand-exempt: MUI palette value (needs a parseable color); brand applied via withBrandTheme
            },
            secondary: {
              main: darkMode ? "#f48fb1" : "#dc004e",
            },
            background: {
              default: darkMode ? "#0f1419" : "#f7f8fa",
              paper: darkMode ? "#1a2332" : "#ffffff",
            },
            ...(darkMode
              ? {
                  text: {
                    primary: "#e8eaed",
                    secondary: "#9aa0a6",
                  },
                  divider: "#2d3b4e",
                }
              : {}),
          },
          components: {
            MuiPaper: {
              styleOverrides: {
                root: {
                  backgroundImage: "none",
                },
              },
            },
          },
          typography: {
            fontFamily: "'Poppins', sans-serif",
            h1: {
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 600,
            },
            h2: {
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 500,
            },
            body1: {
              fontFamily: "'Poppins', sans-serif",
            },
          },
        }, brand.data, darkMode)
      ),
    [darkMode, brand.data]
  );

  const getRoutes = (routes) => {
    return routes?.map((prop, key) => {
      if (prop.layout === "/:CompanyUrl/customers" && !prop.isCollapse) {
        return (
          <Route path={prop?.path} element={prop?.component} key={key} exact />
        );
      } else if (prop.children) {
        return prop.children?.map((item) => (
          <Route
            path={item?.path}
            element={item?.component}
            key={key}
            exact
          />
        ));
      }
      return null;
    });
  };

  return (
    <BrandContext.Provider value={brand}>
    <ThemeProvider theme={portalTheme}>
    <Grid
      ref={scrollShell}
      className={`admin-shell${darkMode ? " admin-shell--dark" : ""}${limoShell}`}
      style={{
        backgroundColor: "var(--app-page-background)",
        overflow: "auto",
        overflowX: "hidden",
      }}
    >
      <Sidebar
        layout={`/:CompanyUrl/customers`}
        isSidebarClosed={isSidebarClosed}
        setIsSidebarClosed={setIsSidebarClosed}
        isSidebarDisplay={isSidebarDisplay}
        setIsSidebarDisplay={setIsSidebarDisplay}
      />

      <MainNav
        layout="/:CompanyUrl/customers"
        isSidebarDisplay={isSidebarDisplay}
        setIsSidebarDisplay={setIsSidebarDisplay}
        isSidebarClosed={isSidebarClosed}
      />
      <Grid
        className="main-content"
        ref={mainContent}
        sx={{
          boxSizing: "border-box",
          marginLeft: !isMediumScreen
            ? isSidebarClosed
              ? `${CJ_PORTAL_SIDEBAR_COLLAPSED_PX}px`
              : `${CJ_PORTAL_SIDEBAR_EXPANDED_PX}px`
            : "15px",
          width: !isMediumScreen
            ? `calc(100% - ${
                isSidebarClosed
                  ? CJ_PORTAL_SIDEBAR_COLLAPSED_PX
                  : CJ_PORTAL_SIDEBAR_EXPANDED_PX
              }px)`
            : "calc(100% - 15px)",
          maxWidth: !isMediumScreen
            ? `calc(100% - ${
                isSidebarClosed
                  ? CJ_PORTAL_SIDEBAR_COLLAPSED_PX
                  : CJ_PORTAL_SIDEBAR_EXPANDED_PX
              }px)`
            : "calc(100% - 15px)",
          minWidth: 0,
          px: { xs: 1.5, sm: 2, md: 4 },
          pb: 2,
        }}
      >
        <Grid sx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", minWidth: 0 }}>
          <React.Suspense fallback={<AdminPageSkeleton minHeight="60vh" />}>
            <Routes>
              {getRoutes(routes)}
              {/* JOB-3220 + JOB-3225: real 404 instead of silent /index redirect */}
              <Route path="*" element={<NotFound homePath={`/${CompanyUrl}/customers/index`} />} />
            </Routes>
          </React.Suspense>
        </Grid>
      </Grid>
    </Grid>
    </ThemeProvider>
    </BrandContext.Provider>
  );
}

const Client = () => (
  <CustomerDarkModeProvider>
    <ClientInner />
  </CustomerDarkModeProvider>
);

export default Client;
